<template>
    <b-modal :visible="value" @change="onVisibleChange" :title="isCreate ? 'Create AR Account' : 'Manage AR Account'"
        size="lg" hide-footer>
        <b-form @submit.prevent="save">
            <b-form-group label="Responsible Patient ID">
                <b-form-input v-model.number="form.responsiblePatientId" type="number" :disabled="!isCreate" />
            </b-form-group>

            <b-form-group label="Default Credit Limit" v-if="isCreate">
                <b-form-input v-model.number="form.defaultCreditLimit" type="number" min="0" step="0.01" />
            </b-form-group>

            <b-form-group label="Statement Delivery Methods">
                <b-form-checkbox-group v-model="form.deliveryMethods" :options="deliveryOptions" stacked />
            </b-form-group>

            <b-form-group label="Notes">
                <b-form-textarea v-model="form.notes" rows="2" />
            </b-form-group>

            <b-form-group label="Linked Patients">
                <div class="d-flex" style="gap: 6px;">
                    <b-form-input v-model.number="linkInput" type="number" placeholder="Patient ID" />
                    <b-button size="sm" variant="outline-primary" @click="addLink">Add</b-button>
                </div>
                <div class="mt-1">
                    <b-badge v-for="pid in form.linkedPatientIds" :key="pid" variant="info" class="mr-1">
                        #{{ pid }}
                        <span style="cursor: pointer;" @click="removeLink(pid)">&times;</span>
                    </b-badge>
                    <span v-if="!form.linkedPatientIds.length" class="text-muted">None</span>
                </div>
            </b-form-group>

            <div class="text-right">
                <b-button variant="secondary" class="mr-2" @click="close">Cancel</b-button>
                <b-button variant="primary" type="submit" :disabled="saving || !canSave">Save</b-button>
            </div>
        </b-form>

        <template v-if="!isCreate">
            <hr />
            <h6>Account Actions</h6>
            <div class="d-flex flex-wrap" style="gap: 6px;">
                <b-button v-if="canClose" size="sm" variant="warning" :disabled="saving" @click="setStatus(2)">
                    Suspend
                </b-button>
                <b-button v-if="canClose" size="sm" variant="secondary" :disabled="saving" @click="setStatus(3)">
                    Close
                </b-button>
                <b-button v-if="canClose" size="sm" variant="danger" :disabled="saving" @click="setStatus(4)">
                    Write Off
                </b-button>
                <b-button v-if="canClose" size="sm" variant="success" :disabled="saving" @click="setStatus(1)">
                    Reactivate
                </b-button>
            </div>

            <div v-if="canOverrideLimit" class="mt-3">
                <label class="mb-1">Credit Limit Override</label>
                <div class="d-flex" style="gap: 6px;">
                    <b-form-input v-model.number="creditLimitInput" type="number" min="0" step="0.01" />
                    <b-button size="sm" variant="outline-primary" :disabled="saving" @click="saveCreditLimit">
                        Set
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import {
    ARAccountSummary,
    ARDeliveryMethodOptions,
    arDeliveryArrayToFlags,
    arDeliveryFlagsToArray,
} from "@/models/AR";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

interface ManageForm {
    responsiblePatientId: number | null;
    defaultCreditLimit: number | null;
    deliveryMethods: number[];
    notes: string;
    linkedPatientIds: number[];
}

@Component({
    name: "ARAccountManageModal",
})
export default class ARAccountManageModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop() patient!: Patient;
    @Prop({ default: null }) accountId!: number | null;

    private saving = false;
    private linkInput: number | null = null;
    private creditLimitInput: number | null = null;
    private deliveryOptions = ARDeliveryMethodOptions;

    private form: ManageForm = this.emptyForm();

    get isCreate(): boolean {
        return !this.accountId;
    }

    get canClose(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/Close");
    }

    get canOverrideLimit(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Credit Limit/Override Account Limit");
    }

    get canSave(): boolean {
        return !!this.form.responsiblePatientId;
    }

    @Watch("value")
    onShow(visible: boolean): void {
        if (!visible) return;
        if (this.isCreate) {
            this.form = this.emptyForm();
            this.form.responsiblePatientId = this.patient?.id ?? null;
        } else {
            this.loadAccount();
        }
    }

    private emptyForm(): ManageForm {
        return {
            responsiblePatientId: null,
            defaultCreditLimit: null,
            deliveryMethods: [],
            notes: "",
            linkedPatientIds: [],
        };
    }

    private async loadAccount(): Promise<void> {
        if (!this.accountId) return;
        try {
            const response = await axios.get<ARAccountSummary>(`/AR/accounts/${this.accountId}`);
            const acct = response.data;
            this.form = {
                responsiblePatientId: acct.responsiblePatientId ?? null,
                defaultCreditLimit: acct.defaultCreditLimit ?? null,
                deliveryMethods: arDeliveryFlagsToArray(acct.statementDeliveryMethods),
                notes: acct.notes ?? "",
                linkedPatientIds: (acct.linkedPatients ?? [])
                    .filter(lp => lp.active !== false && lp.patientId != null)
                    .map(lp => lp.patientId as number),
            };
            this.creditLimitInput = acct.defaultCreditLimit ?? null;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    addLink(): void {
        if (this.linkInput && !this.form.linkedPatientIds.includes(this.linkInput)) {
            this.form.linkedPatientIds.push(this.linkInput);
        }
        this.linkInput = null;
    }

    removeLink(patientId: number): void {
        this.form.linkedPatientIds = this.form.linkedPatientIds.filter(p => p !== patientId);
    }

    private buildPayload() {
        return {
            responsiblePatientId: this.form.responsiblePatientId,
            defaultCreditLimit: this.form.defaultCreditLimit,
            statementDeliveryMethods: this.form.deliveryMethods.length
                ? arDeliveryArrayToFlags(this.form.deliveryMethods)
                : null,
            notes: this.form.notes,
            linkedPatients: this.form.linkedPatientIds.map(id => ({ patientId: id })),
        };
    }

    async save(): Promise<void> {
        if (!this.canSave) return;
        try {
            this.saving = true;
            if (this.isCreate) {
                await axios.post("/AR/accounts", this.buildPayload());
            } else {
                await axios.put(`/AR/accounts/${this.accountId}`, this.buildPayload());
            }
            this.$notification(NotificationOptions.success("AR account saved."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    async setStatus(status: number): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/status`, { status });
            this.$notification(NotificationOptions.success("Account status updated."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    async saveCreditLimit(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/credit-limit`, { creditLimit: this.creditLimitInput });
            this.$notification(NotificationOptions.success("Credit limit updated."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
